import { GetChargePointsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-charge-points-paginated.model';
import { SetChargePointsLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/set-charge-points-location.model';
import { GetLocationsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/locations/models/inputs/get-locations-paginated.model';
import { GatewayLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/locations/models/outputs/gateway-location.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { CreateLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/create-location.model';
import { DeleteLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/delete-location.model';
import { UpdateLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/location/update-location.model';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  LOCATION_FILTERS_LSK,
  LOCATION_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  CHARGE_POINT_LIST_SORT_FIELD,
  CHARGE_POINT_LIST_SORT_ORDER,
} from '../charge-point/charge-point-list/charge-point-list.config';
import {
  LOCATION_LIST_DEFAULT_OPTIONS,
  LOCATION_LIST_FILTER_NAME_LIST,
  LOCATION_LIST_SORT_FIELD,
  LOCATION_LIST_SORT_ORDER,
  LocationListOptionsModel,
} from './location-list/location-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const LocationContext = createContext<GatewayLocationModel | null>(null);

export const useLocationContext = (): GatewayLocationModel => {
  const context = useContext(LocationContext);

  if (!context) {
    throw new Error(
      'useLocationContext must be used within LocationContext.Provider',
    );
  }

  return context;
};

interface LocationContextProviderProps {
  data: GatewayLocationModel;
  children: React.ReactNode;
}

export const LocationContextProvider = ({
  data,
  children,
}: LocationContextProviderProps): React.JSX.Element => {
  return (
    <LocationContext.Provider value={data}>{children}</LocationContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface LocationListContextProps {
  getPaginatedQueryParameters: GetLocationsPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetLocationsPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
  columnOptions: LocationListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<LocationListOptionsModel>>;
}

export const LocationListContext =
  createContext<LocationListContextProps | null>(null);

export const useLocationListContext = (): LocationListContextProps => {
  const context = useContext(LocationListContext);

  if (!context) {
    throw new Error(
      'useLocationListContext must be used within LocationListContext.Provider',
    );
  }

  return context;
};

interface LocationListContextProviderProps {
  children: React.ReactNode;
}

export const LocationListContextProvider = ({
  children,
}: LocationListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetLocationsPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        networkId: [],
        textToSearch: '',
        // Sortable fields
        sortField: LOCATION_LIST_SORT_FIELD,
        sortOrder: LOCATION_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...LOCATION_LIST_FILTER_NAME_LIST,
      ],
      LOCATION_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<LocationListOptionsModel>(
      LOCATION_LIST_DEFAULT_OPTIONS,
      LOCATION_LIST_OPTIONS_LSK,
    );

  const data = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      // setGetPaginatedLimitList,
      columnOptions,
      setColumnOptions,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    // setGetPaginatedLimitList,
    columnOptions,
    setColumnOptions,
  ]);

  return (
    <LocationListContext.Provider value={data}>
      {children}
    </LocationListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterLocationListContext =
  createContext<DeepPartialSkipArrayKey<GetLocationsPaginatedModel> | null>(
    null,
  );

export const useFilterLocationListContext =
  (): DeepPartialSkipArrayKey<GetLocationsPaginatedModel> => {
    const context = useContext(FilterLocationListContext);

    if (!context) {
      throw new Error(
        'useFilterLocationListContext must be used within FilterLocationListContext.Provider',
      );
    }

    return context;
  };

interface FilterLocationListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetLocationsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterLocationListContextProvider = ({
  data,
  children,
}: FilterLocationListContextProviderProps): React.JSX.Element => {
  return (
    <FilterLocationListContext.Provider value={data}>
      {children}
    </FilterLocationListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchLocationListContext =
  createContext<DeepPartialSkipArrayKey<GetLocationsPaginatedModel> | null>(
    null,
  );

export const useFilterSearchLocationListContext =
  (): DeepPartialSkipArrayKey<GetLocationsPaginatedModel> => {
    const context = useContext(FilterSearchLocationListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchLocationListContext must be used within FilterSearchLocationListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchLocationListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetLocationsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchLocationListContextProvider = ({
  data,
  children,
}: FilterSearchLocationListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchLocationListContext.Provider value={data}>
      {children}
    </FilterSearchLocationListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const CreateLocationContext =
  createContext<DeepPartialSkipArrayKey<CreateLocationModel> | null>(null);

export const useCreateLocationContext =
  (): DeepPartialSkipArrayKey<CreateLocationModel> => {
    const context = useContext(CreateLocationContext);

    if (!context) {
      throw new Error(
        'useCreateLocationContext must be used within CreateLocationContext.Provider',
      );
    }

    return context;
  };

interface CreateLocationContextProviderProps {
  data: DeepPartialSkipArrayKey<CreateLocationModel>;
  children: React.ReactNode;
}

export const CreateLocationContextProvider = ({
  data,
  children,
}: CreateLocationContextProviderProps): React.JSX.Element => {
  return (
    <CreateLocationContext.Provider value={data}>
      {children}
    </CreateLocationContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UpdateLocationContext = createContext<Pick<
  UpdateLocationModel,
  'locationId' | 'location'
> | null>(null);

export const useUpdateLocationContext = (): Pick<
  UpdateLocationModel,
  'locationId' | 'location'
> => {
  const context = useContext(UpdateLocationContext);

  if (!context) {
    throw new Error(
      'useUpdateLocationContext must be used within UpdateLocationContext.Provider',
    );
  }

  return context;
};

interface UpdateLocationContextProviderProps {
  data: Pick<UpdateLocationModel, 'locationId' | 'location'>;
  children: React.ReactNode;
}

export const UpdateLocationContextProvider = ({
  data,
  children,
}: UpdateLocationContextProviderProps): React.JSX.Element => {
  return (
    <UpdateLocationContext.Provider value={data}>
      {children}
    </UpdateLocationContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const DeleteLocationContext = createContext<Pick<
  DeleteLocationModel,
  'id'
> | null>(null);

export const useDeleteLocationContext = (): Pick<DeleteLocationModel, 'id'> => {
  const context = useContext(DeleteLocationContext);

  if (!context) {
    throw new Error(
      'useDeleteLocationContext must be used within DeleteLocationContext.Provider',
    );
  }

  return context;
};

interface DeleteLocationContextProviderProps {
  data: Pick<DeleteLocationModel, 'id'>;
  children: React.ReactNode;
}

export const DeleteLocationContextProvider = ({
  data,
  children,
}: DeleteLocationContextProviderProps): React.JSX.Element => {
  return (
    <DeleteLocationContext.Provider value={data}>
      {children}
    </DeleteLocationContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UpdateLocationChargePointsContext = createContext<Pick<
  SetChargePointsLocationModel,
  'chargePointIds' | 'locationId'
> | null>(null);

export const useUpdateLocationChargePointsContext = (): Pick<
  SetChargePointsLocationModel,
  'chargePointIds' | 'locationId'
> => {
  const context = useContext(UpdateLocationChargePointsContext);

  if (!context) {
    throw new Error(
      'useUpdateLocationChargePointsContext must be used within UpdateLocationChargePointsContext.Provider',
    );
  }

  return context;
};

interface UpdateLocationChargePointsContextProviderProps {
  data: SetChargePointsLocationModel;
  children: React.ReactNode;
}

export const UpdateLocationChargePointsContextProvider = ({
  data,
  children,
}: UpdateLocationChargePointsContextProviderProps): React.JSX.Element => {
  return (
    <UpdateLocationChargePointsContext.Provider value={data}>
      {children}
    </UpdateLocationChargePointsContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface LocationChargePointListContextProps {
  getPaginatedQueryParameters: GetChargePointsPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetChargePointsPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
}

export const LocationChargePointListContext =
  createContext<LocationChargePointListContextProps | null>(null);

export const useLocationChargePointListContext =
  (): LocationChargePointListContextProps => {
    const context = useContext(LocationChargePointListContext);

    if (!context) {
      throw new Error(
        'useLocationChargePointListContext must be used within LocationChargePointListContext.Provider',
      );
    }

    return context;
  };

interface LocationChargePointListContextProviderProps {
  data: { location: GatewayLocationModel };
  children: React.ReactNode;
}

export const LocationChargePointListContextProvider = ({
  data,
  children,
}: LocationChargePointListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useState<GetChargePointsPaginatedModel>({
      // Pagination
      limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
      offset: 0,
      // Filterable fields
      locationId: [data.location.id],
      // Sortable fields
      sortField: CHARGE_POINT_LIST_SORT_FIELD,
      sortOrder: CHARGE_POINT_LIST_SORT_ORDER,
    });

  const value = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      // setGetPaginatedLimitList,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    // setGetPaginatedLimitList,
  ]);

  return (
    <LocationChargePointListContext.Provider value={value}>
      {children}
    </LocationChargePointListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface LocationChargePointListUpdateContextProps {
  getPaginatedQueryParameters: GetChargePointsPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetChargePointsPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
}

export const LocationChargePointListUpdateContext =
  createContext<LocationChargePointListUpdateContextProps | null>(null);

export const useLocationChargePointListUpdateContext =
  (): LocationChargePointListUpdateContextProps => {
    const context = useContext(LocationChargePointListContext);

    if (!context) {
      throw new Error(
        'useLocationChargePointListUpdateContext must be used within LocationChargePointListUpdateContext.Provider',
      );
    }

    return context;
  };

interface LocationChargePointListUpdateContextProviderProps {
  data: { location: GatewayLocationModel };
  children: React.ReactNode;
}

export const LocationChargePointListUpdateContextProvider = ({
  data,
  children,
}: LocationChargePointListUpdateContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useState<GetChargePointsPaginatedModel>({
      // Pagination
      limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
      offset: 0,
      // Filterable fields
      hasLocation: false,
      customerId: [data.location.customer.id],
      networkId: [data.location.network.id],
      // Sortable fields
      sortField: CHARGE_POINT_LIST_SORT_FIELD,
      sortOrder: CHARGE_POINT_LIST_SORT_ORDER,
    });

  const value = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      // setGetPaginatedLimitList,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    // setGetPaginatedLimitList,
  ]);

  return (
    <LocationChargePointListContext.Provider value={value}>
      {children}
    </LocationChargePointListContext.Provider>
  );
};
